import React, { useState, useEffect } from 'react';
import { Edit3, Save, X } from 'lucide-react';
import { getAppsIntro, saveAppsIntro } from '../../services/homepageContentService';

const AppsIntro = ({ isAdmin, setMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [intro, setIntro] = useState({
    title: 'My Web Applications',
    content: 'Explore the web applications I\'ve built. Each project represents a unique solution to a real-world problem.'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAppsIntro = async () => {
      try {
        const data = await getAppsIntro();
        if (data) {
          setIntro(data);
        }
      } catch (error) {
        console.error('Error loading apps intro:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAppsIntro();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveAppsIntro(intro);
      setMessage("Apps intro updated successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving apps intro:', error);
      setMessage("Failed to save apps intro. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reload from database to discard changes
    const loadAppsIntro = async () => {
      try {
        const data = await getAppsIntro();
        if (data) {
          setIntro(data);
        }
      } catch (error) {
        console.error('Error reloading apps intro:', error);
      }
    };
    loadAppsIntro();
  };

  const handleInputChange = (field, value) => {
    setIntro(prev => ({ ...prev, [field]: value }));
  };

  if (loading && !intro.content) {
    return (
      <div className="mb-8 p-6 panel">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="mb-8 p-6 panel">
        <h2 className="font-serif text-3xl font-semibold tracking-tight text-stone-900 mb-3">{intro.title}</h2>
        <p className="text-gray-600 whitespace-pre-line">
          {intro.content}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8 p-6 panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-3xl font-semibold tracking-tight text-stone-900">Apps Intro</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={intro.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={intro.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              rows="4"
              className="field"
              placeholder="Enter intro content..."
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-stone-900 mb-3">{intro.title}</h2>
          <p className="text-gray-600 whitespace-pre-line">
            {intro.content}
          </p>
        </div>
      )}
    </div>
  );
};

export default AppsIntro;
